import { hasPerm } from '../../../../portal-role.js'
import type { PortalPermissions } from '../../utils/portal-api.js'
import type { SettingsTab } from './settings-view.js'

interface SettingsSection {
    segment: string
    tab: SettingsTab
    // Admin permission key required to see/use this tab.
    permission: string
}

// Single source of truth for the Settings tab ↔ URL segment ↔ permission
// mapping. List order is the order of the top tab row; the audit log sits last.
export const SETTINGS_SECTIONS: SettingsSection[] = [
    { segment: 'general', tab: 'general', permission: 'manage_organisation_settings' },
    { segment: 'leave-policies', tab: 'leavePolicies', permission: 'manage_time_away_settings' },
    { segment: 'leave-types', tab: 'leaveTypes', permission: 'manage_time_away_settings' },
    { segment: 'holidays', tab: 'holidays', permission: 'manage_time_away_settings' },
    {
        segment: 'working-patterns',
        tab: 'workingPatterns',
        permission: 'manage_time_away_settings',
    },
    { segment: 'teams', tab: 'teams', permission: 'manage_organisation_settings' },
    { segment: 'locations', tab: 'locations', permission: 'manage_organisation_settings' },
    { segment: 'custom-fields', tab: 'customFields', permission: 'manage_custom_fields' },
    {
        segment: 'compensation-types',
        tab: 'compensationTypes',
        permission: 'manage_organisation_settings',
    },
    { segment: 'roles', tab: 'roles', permission: 'manage_roles' },
    { segment: 'view-as', tab: 'viewAs', permission: 'view_as' },
    { segment: 'audit-log', tab: 'auditLog', permission: 'view_audit_log' },
    { segment: 'error-log', tab: 'errorLog', permission: 'view_error_log' },
]

// Maps a /settings/:tab segment onto its SettingsTab. Unknown → null (the route
// uses null to redirect to the first allowed tab).
export const tabForSegment = (segment: string | undefined): SettingsTab | null =>
    SETTINGS_SECTIONS.find((entry) => entry.segment === segment)?.tab ?? null

// Reverse lookup for building tab hrefs.
export const segmentForTab = (tab: SettingsTab): string =>
    SETTINGS_SECTIONS.find((entry) => entry.tab === tab)?.segment ?? SETTINGS_SECTIONS[0].segment

// The sections the held admin permission keys allow, in tab-row order.
export const allowedSections = (permissions: PortalPermissions): SettingsSection[] =>
    SETTINGS_SECTIONS.filter((entry) => hasPerm(permissions, 'admin', entry.permission))
